import { useEffect } from 'react';

// import { useQueryClient } from '@tanstack/react-query';
import { webSocketsService } from '../../../application/WebSocketsService';
import { Route } from '../../../domain/route/Route';
import { RouteMapper } from '../../../mappers/RouteMapper';
import { useRouteContext } from '../route/context/context';
import { useRetrieveRouteRaw } from '../route/hooks/useRetrieveRouteRaw';
import { useNotification } from './useNotification';
import { useRetrieveVehicle } from './useRetrieveVehicle';

const getSubscriptionVehiclePath = (vehicleId: string) => `/vehicle/${vehicleId}`;

interface newRouteDataDTO {
  logEntry_id: string;
  vehicle_id: string;
  driver_id: string;
  route: any;
}

// Hooks act as 'Adapter' layer.
export const useVehicleWebSockets = () => {
  const { data: vehicleData } = useRetrieveVehicle();
  const { data: route } = useRetrieveRouteRaw();
  const { routeState } = useRouteContext();
  const { setNotification } = useNotification();

  useEffect(() => {
    if (route && newRouteChanged(route)) {
      setNotification({
        id: `smartmonkey.planner.2.planner.stop.rescheduled`,
        data: route,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const newRouteChanged = (newRoute: Route): boolean => {
    if (!routeState.route) return false;
    const newStops = newRoute.stops;
    const oldStops = routeState.route!.stops;

    if (oldStops.length !== newStops.length) return true;

    const hasDifferentId = newStops.some((newStop, index) => newStop.id !== oldStops[index].id);
    if (hasDifferentId) return true;

    return false;
  };

  useEffect(() => {
    if (!vehicleData || webSocketsService.getSubscription(getSubscriptionVehiclePath(vehicleData.vehicle.id))) {
      return;
    }

    const handleConnect = () => {
      webSocketsService.subscribe(
        getSubscriptionVehiclePath(vehicleData.vehicle.id),
        async ({ id, created_at, data, meta }) => {
          switch (id) {
            case `smartmonkey.planner.2.planner.stop.reported`:
            case `smartmonkey.planner.2.planner.stop.deleted`:
            case `smartmonkey.planner.2.planner.stop.created`:
            case `smartmonkey.planner.2.planner.stop.rescheduled`:
            case `smartmonkey.planner.2.planner.stop.moved`:
            case `smartmonkey.planner.2.planner.stop.updated`:
            case `smartmonkey.planner.2.planner.driver.deleted`: {
              setNotification({
                id,
                data: RouteMapper.toDomain(data),
              });
              break;
            }
            // `smartmonkey.planner.2.planner.plan.deleted`,
            // `smartmonkey.planner.2.planner.plan.updated`, // MAYBE PLAN STATUS CHANGED (when draft, published, etc. status refactor is done)
            case `smartmonkey.planner.2.optimizer.find_new_route_optimization.succeeded`: {
              console.log(`Route optimization succeeded`, data);
              if (data) {
                const msg = data as newRouteDataDTO;
                msg.route = RouteMapper.toDomain(msg.route);
                setNotification({
                  id,
                  data: msg,
                });
              }
              break;
            }
            default:
              break;
          }
        }
      );
    };

    webSocketsService.on(`connection_event`, (e: CustomEvent<string>) => {
      handleConnect();
    });

    return () => {
      if (vehicleData?.vehicle) {
        webSocketsService.unsubscribe(getSubscriptionVehiclePath(vehicleData?.vehicle.id));
      }
      webSocketsService.off(`connection_event`, handleConnect);
      webSocketsService.close(); // This is to close the socket when the component unmounts, so it does not remain pinging
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleData?.vehicle.id]);
};
